import React from "react";
import { Spinner } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { PurchaseApi, TaxItemApi } from "../../../../../../../../../../../../services/Api/Organization/types";

interface Props {
	summaryTaxes: TaxItemApi[];
	exchangeRateLoading: boolean;
	form: UseFormReturn<PurchaseApi>;
	exchangeRate: number;
}

const PurchaseFormItemsSummaryTableBody = ({ summaryTaxes, form, exchangeRateLoading, exchangeRate }: Props) => {
	const { t } = useTranslation();
	const { watch } = form;
	const organization = useSelector(selectOrganization);
	const taxesSummary = summaryTaxes.reduce(
		(acc, val) => acc + (val.price_tax?.amount ? val.price_tax?.amount : 0),
		0
	);
	const grossSummary = summaryTaxes.reduce(
		(acc, val) => acc + (val.price_gross?.amount ? val.price_gross?.amount : 0),
		0
	);
	const netSummary = summaryTaxes.reduce((acc, val) => acc + (val.price_net?.amount ? val.price_net?.amount : 0), 0);
	const watchedCurrency = watch("currency");
	const organizationCurrency = organization?.currency || "";

	return (
		<tbody>
			{summaryTaxes.map((tax, index) => (
				<tr key={`tax-row-${index}`}>
					<td>{FormatMoney({ ...tax.price_net, currency: watchedCurrency })}</td>
					<td>{tax.name ?? tax.tax_amount}</td>
					<td>{FormatMoney({ ...tax.price_tax, currency: watchedCurrency })}</td>
					<td className="text-end" colSpan={2}>
						{FormatMoney({ ...tax.price_gross, currency: watchedCurrency })}
					</td>
				</tr>
			))}
			<tr className="no-padding-bottom">
				<td colSpan={5} className="text-start">
					<strong>{t("modules.purchase.field.all.title")}:</strong>
				</td>
			</tr>
			<tr className="no-padding-top">
				<td colSpan={2}>
					<strong>{FormatMoney({ amount: netSummary, currency: watchedCurrency })}</strong>
				</td>
				<td>
					<strong>{FormatMoney({ amount: taxesSummary, currency: watchedCurrency })}</strong>
				</td>
				<td colSpan={2}>
					<strong>{FormatMoney({ amount: grossSummary, currency: watchedCurrency })}</strong>
				</td>
			</tr>
			{organization?.currency !== watchedCurrency && (
				<>
					<tr className="no-padding-top">
						{exchangeRateLoading ? (
							<td colSpan={5} className="text-end">
								<Spinner
									as="span"
									animation="border"
									size="sm"
									role="status"
									aria-hidden="true"
									className="me-1"
								/>
								<span>{t("modules.purchase.field.exchange_loading.title")}</span>
							</td>
						) : (
							<>
								<td colSpan={2}>
									<strong>
										{FormatMoney({
											amount: netSummary * exchangeRate,
											currency: organizationCurrency,
										})}
									</strong>
								</td>
								<td>
									<strong>
										{FormatMoney({
											amount: taxesSummary * exchangeRate,
											currency: organizationCurrency,
										})}
									</strong>
								</td>
								<td colSpan={2}>
									<strong>
										{FormatMoney({
											amount: grossSummary * exchangeRate,
											currency: organizationCurrency,
										})}
									</strong>
								</td>
							</>
						)}
					</tr>
					{!exchangeRateLoading && (
						<tr className="no-padding-top">
							<td colSpan={2}>
								<strong>
									{t("modules.purchase.field.currency_converter.title")} ({organization?.currency})
								</strong>
							</td>
							<td />
							<td colSpan={2} className="text-end">
								<strong>
									1 {watchedCurrency} - {exchangeRate.toFixed(2)} {organization?.currency}
								</strong>
							</td>
						</tr>
					)}
				</>
			)}
		</tbody>
	);
};

export default PurchaseFormItemsSummaryTableBody;
